/**
 * Render the PlotObject graph to GeoJSON for MapLibre. Pure and unit-testable:
 * it takes objects, returns features tagged with a `role` the map layer styles,
 * plus a list of paper-chart labels.
 *
 * Styling intent, paper-chart convention:
 *   - line of position: thin line through the mark, extended both ways.
 *   - range arc: thin circle around the mark.
 *   - water track: solid line, single arrow.
 *   - tidal vector: line with three arrows.
 *   - ground track: solid line, double arrow.
 *   - DR: a dot with a single bar. EP: a dot in a triangle. Fix: a circle.
 *   - cocked hat: faint filled triangle, the honest measure of the error.
 *
 * Vectors need their start point's position, so we index the points first, then
 * walk the vectors. A vector's length is a fixed distance, or rate by time.
 */
import type { Feature, FeatureCollection, Position } from 'geojson';
import { rhumbDestination, normaliseBearing } from '@/nav';
import type { PlotObject, Vector } from './objects';

export interface RenderedLabel {
  text: string;
  position: [number, number];
}

export interface RenderedObjects {
  features: FeatureCollection;
  labels: RenderedLabel[];
}

/** Half-length of a drawn line of position, in nm, each side of the mark. */
const LOP_HALF_NM = 8;
/** Points used to approximate a range circle. */
const CIRCLE_STEPS = 72;

const VECTOR_ROLE: Record<Vector['kind'], string> = {
  water_track: 'water-track',
  tidal_vector: 'tide',
  ground_track: 'ground-track',
  leeway_vector: 'leeway',
  set_drift_vector: 'set-drift',
};

export function objectsToGeoJSON(objects: PlotObject[]): RenderedObjects {
  const features: Feature[] = [];
  const labels: RenderedLabel[] = [];

  // Index point positions first, so vectors can resolve their start.
  const pointPos = new Map<string, [number, number]>();
  for (const obj of objects) {
    if (obj.kind === 'position_point') pointPos.set(obj.id, obj.position);
  }

  for (const obj of objects) {
    switch (obj.kind) {
      case 'bearing_lop':
      case 'transit_lop':
      case 'advanced_lop': {
        if (obj.geometry.type !== 'bearing') break;
        const { from, bearingDeg } = obj.geometry;
        const a = rhumbDestination(from, bearingDeg, LOP_HALF_NM);
        const b = rhumbDestination(from, normaliseBearing(bearingDeg + 180), LOP_HALF_NM);
        features.push(line([b, a], obj.id, obj.kind === 'advanced_lop' ? 'transferred' : 'lop'));
        if (obj.label) labels.push({ text: obj.label, position: from });
        break;
      }
      case 'range_arc': {
        if (obj.geometry.type !== 'range') break;
        features.push(circle(obj.geometry.centre, obj.geometry.rangeNm, obj.id));
        if (obj.label) labels.push({ text: obj.label, position: obj.geometry.centre });
        break;
      }
      case 'water_track':
      case 'tidal_vector':
      case 'ground_track':
      case 'leeway_vector':
      case 'set_drift_vector': {
        const startPos = pointPos.get(obj.start);
        if (!startPos) break;
        const len = obj.distanceNm ?? (obj.rateKnots ?? 0) * (obj.interval?.hours ?? 0);
        const end = rhumbDestination(startPos, obj.directionDeg, len);
        features.push(line([startPos, end], obj.id, VECTOR_ROLE[obj.kind]));
        if (obj.label) {
          labels.push({
            text: obj.label,
            position: [(startPos[0] + end[0]) / 2, (startPos[1] + end[1]) / 2],
          });
        }
        break;
      }
      case 'position_point': {
        features.push(point(obj.position, obj.id, pointRole(obj.pointType)));
        if (obj.label) labels.push({ text: obj.label, position: obj.position });
        break;
      }
      case 'uncertainty_zone': {
        if (!obj.polygon || obj.polygon.length < 3) break;
        features.push(polygon(obj.polygon, obj.id));
        break;
      }
      default:
        // Other kinds (CTS, clearing objects) render in later slices.
        break;
    }
  }

  return { features: { type: 'FeatureCollection', features }, labels };
}

function pointRole(pointType: string): string {
  if (pointType === 'fix' || pointType === 'running_fix') return 'fix-mark';
  if (pointType === 'dr') return 'dr-mark';
  if (pointType === 'ep') return 'ep-mark';
  return 'point-mark';
}

function line(coords: Position[], id: string, role: string): Feature {
  return {
    type: 'Feature',
    geometry: { type: 'LineString', coordinates: coords },
    properties: { objectId: id, role },
  };
}

function point(pos: [number, number], id: string, role: string): Feature {
  return {
    type: 'Feature',
    geometry: { type: 'Point', coordinates: pos },
    properties: { objectId: id, role },
  };
}

function circle(centre: [number, number], radiusNm: number, id: string): Feature {
  const ring: Position[] = [];
  for (let i = 0; i <= CIRCLE_STEPS; i++) {
    const brg = (360 / CIRCLE_STEPS) * i;
    ring.push(rhumbDestination(centre, brg, radiusNm));
  }
  return {
    type: 'Feature',
    geometry: { type: 'LineString', coordinates: ring },
    properties: { objectId: id, role: 'range-circle' },
  };
}

function polygon(ring: [number, number][], id: string): Feature {
  const closed: Position[] = [...ring];
  const first = ring[0];
  const last = ring[ring.length - 1];
  if (first[0] !== last[0] || first[1] !== last[1]) closed.push(first);
  return {
    type: 'Feature',
    geometry: { type: 'Polygon', coordinates: [closed] },
    properties: { objectId: id, role: 'cocked-hat' },
  };
}
